import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import useApi from '../../hooks/api/axiosInterceptor';

// formatDate 함수를 컴포넌트 내부에 직접 구현
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  // 날짜 형식이 'YYYY-MM-DD'인 경우
  if (typeof dateString === 'string' && dateString.length >= 10) {
    return dateString.substring(0, 10); // YYYY-MM-DD 부분만 반환
  }
  
  // Date 객체로 변환 가능한 경우
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch (e) {
    console.error('날짜 형식 변환 오류:', e);
  }
  
  return dateString; // 변환 실패 시 원본 반환
};

const BoardShortcut = () => {
  const [newsList, setNewsList] = useState([]);
  const [activityList, setActivityList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 총학생회 소식 최신글 5개 가져오기
        const newsResponse = await useApi.get('/dbtest', {
          params: {
            board: 'student-council',
            limit: 5,
            offset: 0
          }
        });
        
        // 자치활동보고 최신글 5개 가져오기
        const activityResponse = await useApi.get('/dbtest', {
          params: {
            board: 'activity',
            limit: 5,
            offset: 0
          }
        });
        
        if (newsResponse.data.result === 'SUCCESS') {
          setNewsList(newsResponse.data.data || []);
        }
        
        if (activityResponse.data.result === 'SUCCESS') {
          setActivityList(activityResponse.data.data || []);
        }
      } catch (error) {
        console.error('게시판 데이터 로딩 중 오류 발생:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <BoardShortcutContainer>
      <BoardSection>
        <SectionHeader>
          <SectionTitle>총학생회 소식</SectionTitle>
          <MoreButton to="/news/student-council">더보기 +</MoreButton>
        </SectionHeader>

        <BoardListContainer>
          {loading ? (
            <LoadingText>게시글을 불러오는 중입니다...</LoadingText>
          ) : (
            <PostList>
              {newsList.length > 0 ? (
                newsList.map((post) => (
                  <PostItem key={post.id}>
                    <PostLink to={`/news/student-council/detail/${post.id}`}>
                      <PostTitle>{post.title}</PostTitle>
                      <PostDate>{formatDate(post.createdAt)}</PostDate>
                    </PostLink>
                  </PostItem>
                ))
              ) : (
                <NoDataText>게시글이 없습니다.</NoDataText>
              )}
            </PostList>
          )}
        </BoardListContainer>
      </BoardSection>

      <BoardSection>
        <SectionHeader>
          <SectionTitle>자치활동보고</SectionTitle>
          <MoreButton to="/student-autonomy/activity">더보기 +</MoreButton>
        </SectionHeader>

        <BoardListContainer>
          {loading ? (
            <LoadingText>게시글을 불러오는 중입니다...</LoadingText>
          ) : (
            <PostList>
              {activityList.length > 0 ? (
                activityList.map((post) => (
                  <PostItem key={post.id}>
                    <PostLink to={`/student-autonomy/activity/detail/${post.id}`}>
                      <PostTitle>{post.title}</PostTitle>
                      <PostDate>{formatDate(post.createdAt)}</PostDate>
                    </PostLink>
                  </PostItem>
                ))
              ) : (
                <NoDataText>게시글이 없습니다.</NoDataText>
              )}
            </PostList>
          )}
        </BoardListContainer>
      </BoardSection>
    </BoardShortcutContainer>
  );
};

// 스타일 컴포넌트
const BoardShortcutContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 20px;
  max-width: 1000px; // YouTube 섹션과 동일한 max-width
  margin: 40px auto;
  padding: 0 20px; // YouTube 섹션과 동일한 좌우 패딩
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BoardSection = styled.div`
  flex: 1;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f9f9f9;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2056ad;
`;

const BoardListContainer = styled.div`
  padding: 15px;
  min-height: 250px;
`;

const PostList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const PostItem = styled.li`
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const PostLink = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  color: inherit;
`;

const PostTitle = styled.span`
  flex: 1;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PostDate = styled.span`
  font-size: 12px;
  color: #888;
  margin-left: 10px;
`;

const LoadingText = styled.div`
  text-align: center;
  color: #888;
  padding: 20px 0;
`;

const NoDataText = styled.div`
  text-align: center;
  color: #888;
  padding: 20px 0;
`;

const MoreButton = styled(Link)`
  font-size: 14px;
  color: #2056ad;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

export default BoardShortcut;